import { apiDelete, apiGet, apiPost } from "@/api/client";
import type {
    CharacterSheetResponse,
    ConsumeItemResponse,
    EquipItemRequest,
    EquipmentResponse,
    EquipmentSlot,
    InventoryItemView,
    InventoryResponse,
    StatSheetResponse,
} from "@/api/types";

export function getInventory(runId: string): Promise<InventoryResponse> {
    return apiGet<InventoryResponse>(`/api/v1/public/game/runs/${encodeURIComponent(runId)}/inventory`);
}

export function getEquipment(runId: string): Promise<EquipmentResponse> {
    return apiGet<EquipmentResponse>(`/api/v1/public/game/runs/${encodeURIComponent(runId)}/equipment`);
}

export function equipItem(runId: string, slot: EquipmentSlot, body: EquipItemRequest): Promise<EquipmentResponse> {
    return apiPost<EquipmentResponse>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/equipment/${slot}`,
        body,
    );
}

export function unequipItem(runId: string, slot: EquipmentSlot): Promise<EquipmentResponse> {
    return apiDelete<EquipmentResponse>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/equipment/${slot}`,
    );
}

export function getStatSheet(runId: string): Promise<StatSheetResponse> {
    return apiGet<StatSheetResponse>(`/api/v1/public/game/runs/${encodeURIComponent(runId)}/stats`);
}

export function getCharacterSheet(runId: string): Promise<CharacterSheetResponse> {
    return apiGet<CharacterSheetResponse>(`/api/v1/public/game/runs/${encodeURIComponent(runId)}/character-sheet`);
}

export function useConsumable(runId: string, instanceId: string): Promise<ConsumeItemResponse> {
    return apiPost<ConsumeItemResponse>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/consumables/${encodeURIComponent(instanceId)}/use`,
        {},
    );
}

/**
 * 2026-04-24: 각인 제련 결과.
 *   success=true: item.engraveId 가 요청값으로 갱신.
 *   success=false: 등급별 RNG 실패 (standard 20% / refined 10% / crystal 3%).
 *     재료는 이미 소모, gold 는 안 씀. 플레이어는 다시 시도 가능.
 *   2026-04-24 Phase 31: greatSuccess — 성공 시 재료 보존 RNG.
 *     standard 5% / refined 8% / crystal 12%. true 면 재료가 다시 인벤토리에 남음.
 */
export interface EngraveForgeResult {
    item: InventoryItemView;
    success: boolean;
    materialGradeUsed: "" | "standard" | "refined" | "crystal";
    failureReason?: string | null;
    greatSuccess?: boolean;
}

/**
 * 2026-04-24: 장비 인스턴스에 각인 부여/교체. engraveId=null 이면 제거.
 *   materialInstanceId 제공 시 재료 1개 소모 + 등급별 할인.
 *   응답에 성공/실패 여부 포함 (O-1 제련 RNG).
 */
export function setEngrave(
    runId: string,
    instanceId: string,
    engraveId: string | null,
    materialInstanceId?: string | null,
): Promise<EngraveForgeResult> {
    return apiPost<EngraveForgeResult>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/inventory/${encodeURIComponent(instanceId)}/engrave`,
        { engraveId, materialInstanceId: materialInstanceId ?? null },
    );
}
